import { useState } from "react";
import { AuthenticationMethod } from "../DataTypes/storeTypes";
import { post } from "../../../Common/fetch";
import { Button, DatePicker } from "@powerdiary/components-library/lib";
import { DateTime } from "luxon";

interface IProps {
    authenticationMethod: AuthenticationMethod;
    authenticationErrorMessage: string;
    cultureDateFormat: string;
    encryptTokenUrl: string;
}

const Auth = (props: IProps) => {
    const { authenticationErrorMessage, authenticationMethod, cultureDateFormat, encryptTokenUrl } = props;

    const [credentials, setCredentials] = useState("");
    const [credentialsValid, setCredentialsValid] = useState({
        isValid: !authenticationErrorMessage,
        errorMessage: authenticationErrorMessage,
    });

    const isDobAuth = authenticationMethod === AuthenticationMethod.DateOfBirth;
    const labelText = isDobAuth ? "Enter your date of birth" : "Enter your email address or mobile number";
    const [pickerOpen, setPickerOpen] = useState(false);

    const handleSubmit = (event) => {
        event.preventDefault();

        post<any>(encryptTokenUrl, { token: credentials })
            .then((response) => {
                const queryParam: string = "token";

                const url: URL = new URL(window.location.href);
                const searchParams: URLSearchParams = url.searchParams;
                searchParams.set(queryParam, response);

                window.location.replace(url);
            })
            .catch((serverResponse) => {
                updateErrorState(
                    false,
                    "The details you have entered are incorrect. Please try again, or contact the clinic for assistance."
                );
            });
    };

    const handleNameChange = (event) => {
        credentialsValidation(event.target.value);
        setCredentials(event.target.value);
    };

    const credentialsValidation = (value) => {
        const isValid: boolean = value.length > 0;

        if (isValid === false) {
            updateErrorState(isValid, "This field is required");
            return isValid;
        }
        updateErrorState(isValid, "");
        return isValid;
    };

    const updateErrorState = (isValid, errorMessage) => {
        setCredentialsValid({
            isValid: isValid,
            errorMessage: errorMessage,
        });
    };

    return (
        <div className="custom-form-auth-container">
            <h4 className="header">Secure Client Form</h4>
            <p className="description">Please enter the information below to gain access to the form</p>

            <form onSubmit={handleSubmit}>
                <div className="form-group">
                    <label className="label-text" htmlFor="credentials">
                        {labelText}
                    </label>
                    {isDobAuth ? (
                        <DatePicker
                            label={labelText}
                            value={credentials ? DateTime.fromISO(credentials) : null}
                            onChange={(newValue: DateTime | null) => {
                                const isoDate = newValue?.isValid ? newValue.toISODate() : "";
                                credentialsValidation(isoDate);
                                setCredentials(isoDate);
                            }}
                            format={cultureDateFormat || undefined}
                            open={pickerOpen}
                            onOpen={() => setPickerOpen(true)}
                            onClose={() => setPickerOpen(false)}
                            fullWidth
                            slotProps={{
                                textField: {
                                    onClick: () => setPickerOpen(true),
                                    error: credentialsValid?.isValid === false,
                                    helperText:
                                        credentialsValid?.isValid === false ? credentialsValid.errorMessage : undefined,
                                    id: "credentials",
                                },
                            }}
                        />
                    ) : (
                        <>
                            <input
                                type="text"
                                className={`form-control ${credentialsValid?.isValid === false ? "is-invalid" : ""}`}
                                id="credentials"
                                placeholder={labelText}
                                value={credentials}
                                onChange={handleNameChange}
                            />
                            {credentialsValid?.isValid === false && (
                                <div className="invalid-feedback">{credentialsValid.errorMessage}</div>
                            )}
                        </>
                    )}
                </div>
                <div className="button-wrapper">
                    <Button
                        variant="contained"
                        type="submit"
                        size="small"
                        disabled={credentialsValid?.isValid === false}
                    >
                        Continue
                    </Button>
                </div>
            </form>
        </div>
    );
};

export default Auth;
